import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import {
    fwTaskControlCopy as copy,
    endpoint,
    fwDeviceStatusColors,
    fwDeviceStatusLabels,
    fwTaskStatus,
    fwTaskStatusLabels,
    fwTaskStatusValue,
    fwTaskTypeLabels,
    snackbarDefaultValue,
    snackbarVariant,
    tableTranslations,
} from '../../utils/constants';
import { recordTaskRetirement } from '../../utils/fwTaskControlResult';
import {
    isCancelled,
    isDisabledRefusal,
    operationKey,
    probeWorkflow,
    refusalOf,
    refusalText,
    workflowModes,
} from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Modal from '../Modal/Modal';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';
import FwEndpointBoard from './FwEndpointBoard';
import FwTaskStarts from './FwTaskStarts';

const identifier = value => {
    if (Number.isSafeInteger(value) && value > 0) return String(value);
    return typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;
};
const validVersion = value => Number.isSafeInteger(value) && value > 0 && value < Number.MAX_SAFE_INTEGER;
const workflowStatuses = ['active', 'paused', 'finished', 'cancelled'];
const currentCycle = task => {
    const cycleId = identifier(task?.current_cycle_id);
    if (!cycleId || !Array.isArray(task?.cycles)) return null;
    const matches = task.cycles.filter(cycle => identifier(cycle?.id) === cycleId);
    return matches.length === 1 && identifier(matches[0]?.task_id) === identifier(task.id)
        ? matches[0]
        : null;
};
const membershipCount = task => {
    if (!Array.isArray(task?.devices)) return null;
    const ids = task.devices.map(device => identifier(device?.id));
    return ids.every(Boolean) && new Set(ids).size === ids.length ? ids.length : null;
};
const controlAllowed = (task, action) => {
    if (action === 'finish') return ['active', 'paused'].includes(task?.status);
    if (action === 'retire') return ['finished', 'cancelled'].includes(task?.status);
    if (action === 'pause') return task?.status === 'active' && Boolean(currentCycle(task));
    if (action === 'resume')
        return task?.status === 'paused' && Boolean(currentCycle(task)) && membershipCount(task) > 0;
    return false;
};
const validAllowance = (value, population) =>
    /^\d+$/.test(value) &&
    Number.isSafeInteger(Number(value)) &&
    Number(value) >= 1 &&
    Number(value) <= population;
const controlCopy = {
    finish: { confirm: copy.confirmFinish, effect: copy.finishEffect, outcome: copy.finished },
    retire: { confirm: copy.confirmRetire, effect: copy.retireEffect, outcome: copy.retired },
    pause: { confirm: copy.confirmPause, effect: copy.pauseEffect, outcome: copy.paused },
    resume: { confirm: copy.confirmResume, effect: copy.resumeEffect, outcome: copy.resumed },
};
const controlErrorText = refusal =>
    refusal.fieldCode === 'FW_ALLOWANCE_OUT_OF_RANGE'
        ? copy.allowanceInvalid
        : refusal.message === 'FW_TASK_NOT_FOUND'
          ? copy.taskNotFound
          : refusalText(refusal, copy.unknown);

const styles = {
    root: {
        margin: '1rem 2rem',
    },
    info: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '2rem',
        marginBottom: '1.5rem',
    },
    actions: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '1rem',
        marginBottom: '2rem',
    },
    table: {
        fontFamily: 'inherit',
        width: '100%',
        marginBottom: '2rem',
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '50vh',
    },
};

const FwTaskDetail = ({ classes }) => {
    const { id } = useParams();
    const routeId = identifier(id);
    const history = useHistory();
    const [task, setTask] = useState(null);
    const [mode, setMode] = useState(workflowModes.LOADING);
    const [banner, setBanner] = useState(null);
    const [outcome, setOutcome] = useState(null);
    const [confirmation, setConfirmation] = useState(null);
    const [phase, setPhase] = useState('editing');
    const [allowance, setAllowance] = useState('');
    const [busy, setBusy] = useState(false);
    const [readRevision, setReadRevision] = useState(0);
    const [isFinishModalOpen, setIsFinishModalOpen] = useState(false);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);
    const lifetime = useRef({ active: false, generation: 0 });
    const latestId = useRef(routeId);
    latestId.current = routeId;
    const readSequence = useRef(0);
    const readSource = useRef(null);
    const mutationSource = useRef(null);
    const mutationOwner = useRef(null);
    const frozenOperation = useRef(null);
    const workflowIdentity = useRef(null);
    const cancelButton = useRef(null);

    const current = owner =>
        lifetime.current.active &&
        lifetime.current.generation === owner.generation &&
        latestId.current === owner.taskId;
    const showMessage = (message, variant) =>
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);
    const visibleTask = task && identifier(task.id) === routeId ? task : null;

    const loadTask = async () => {
        if (mutationOwner.current || !lifetime.current.active) return;
        const owner = { generation: lifetime.current.generation, taskId: latestId.current };
        const sequence = ++readSequence.current;
        const active = () => current(owner) && sequence === readSequence.current;
        readSource.current?.cancel();
        readSource.current = cancelToken.source();
        const config = { cancelToken: readSource.current.token };
        setTask(null);
        setBanner(null);
        setMode(workflowModes.LOADING);
        setConfirmation(null);
        setIsFinishModalOpen(false);
        setPhase('editing');
        setAllowance('');
        frozenOperation.current = null;
        if (!owner.taskId) {
            setMode(workflowModes.ERROR);
            setBanner(copy.invalid);
            return;
        }
        const probe = await probeWorkflow(config);
        if (!active()) return;
        if (probe.mode !== workflowModes.CONFIGURED && probe.mode !== workflowModes.DISABLED) {
            setMode(workflowModes.ERROR);
            setBanner(refusalText(probe.refusal, copy.readError));
            return;
        }
        if (probe.mode === workflowModes.DISABLED && workflowIdentity.current === owner.taskId) {
            setMode(workflowModes.DISABLED);
            setBanner(refusalText(probe.refusal, copy.readError));
            return;
        }
        try {
            const response = await axiosInstance.get(`${endpoint.FW_TASKS}/${owner.taskId}`, config);
            if (!active()) return;
            const data = response?.data;
            const matching = data && identifier(data.id) === owner.taskId;
            const valid =
                probe.mode === workflowModes.CONFIGURED
                    ? matching && validVersion(data.version) && workflowStatuses.includes(data.status)
                    : matching && data.version == null && [1, 2, 3, 4].includes(data.status);
            if (!valid) {
                setMode(workflowModes.ERROR);
                setBanner(copy.invalid);
                return;
            }
            if (probe.mode === workflowModes.CONFIGURED) workflowIdentity.current = owner.taskId;
            setTask(data);
            setMode(probe.mode);
            setReadRevision(value => value + 1);
        } catch (error) {
            if (!active() || isCancelled(error)) return;
            const refusal = refusalOf(error);
            setMode(refusal.status === 404 ? 'not_found' : workflowModes.ERROR);
            setBanner(refusal.status === 404 ? copy.missing : refusalText(refusal, copy.readError));
        }
    };

    useEffect(() => {
        lifetime.current.active = true;
        lifetime.current.generation += 1;
        workflowIdentity.current = null;
        mutationOwner.current = null;
        frozenOperation.current = null;
        setBusy(false);
        setTask(null);
        setOutcome(null);
        setBanner(null);
        setConfirmation(null);
        setIsFinishModalOpen(false);
        setSnackbarData(snackbarDefaultValue);
        loadTask();
        return () => {
            lifetime.current.active = false;
            lifetime.current.generation += 1;
            readSource.current?.cancel();
            mutationSource.current?.cancel();
            mutationOwner.current = null;
            frozenOperation.current = null;
        };
    }, [routeId]);

    const begin = action => {
        if (
            mutationOwner.current ||
            !visibleTask ||
            !lifetime.current.active ||
            identifier(visibleTask.id) !== latestId.current
        )
            return null;
        const owner = { generation: lifetime.current.generation, taskId: latestId.current, action };
        mutationOwner.current = owner;
        mutationSource.current = cancelToken.source();
        owner.token = mutationSource.current.token;
        setBusy(true);
        return owner;
    };
    const owns = owner => mutationOwner.current === owner && current(owner);
    const finishOwner = owner => {
        if (mutationOwner.current === owner) {
            mutationOwner.current = null;
            if (current(owner)) setBusy(false);
        }
    };

    const runLegacy = async action => {
        if (mode !== workflowModes.DISABLED || workflowIdentity.current === routeId || confirmation) return;
        const owner = begin(action);
        if (!owner) return;
        try {
            const url = `${endpoint.FW_TASKS}/${owner.taskId}`;
            if (action === 'delete') await axiosInstance.delete(url);
            else await axiosInstance.post(url + (action === 'finish' ? '/finish' : '/refresh-statuses'));
            if (!owns(owner)) return;
            if (action === 'delete') history.push('/fw-tasks');
            else {
                showMessage(
                    action === 'finish' ? 'Zadanie zakończone' : 'Statusy odświeżone',
                    snackbarVariant.SUCCESS
                );
                finishOwner(owner);
                await loadTask();
            }
        } catch (error) {
            if (!owns(owner) || isCancelled(error)) return;
            setBanner(controlErrorText(refusalOf(error)));
            setTask(null);
        } finally {
            finishOwner(owner);
        }
    };
    const startControl = action => {
        if (
            mutationOwner.current ||
            confirmation ||
            mode !== workflowModes.CONFIGURED ||
            !visibleTask ||
            !controlAllowed(visibleTask, action)
        )
            return;
        frozenOperation.current = null;
        setOutcome(null);
        setBanner(null);
        setPhase('editing');
        setAllowance('');
        setConfirmation({
            taskId: routeId,
            action,
            version: visibleTask.version,
            name: visibleTask.name,
            status: visibleTask.status,
            cycleId: identifier(currentCycle(visibleTask)?.id),
            population: membershipCount(visibleTask),
        });
    };
    const dismissControl = () => {
        if (mutationOwner.current) return;
        frozenOperation.current = null;
        setConfirmation(null);
        if (phase !== 'editing') {
            setTask(null);
            setMode(workflowModes.ERROR);
            setBanner(previous => previous || copy.reloadRequired);
        } else setBanner(null);
        setPhase('editing');
    };
    const sendControl = async () => {
        if (mutationOwner.current) return;
        if (!frozenOperation.current) {
            if (
                !confirmation ||
                phase !== 'editing' ||
                mode !== workflowModes.CONFIGURED ||
                confirmation.taskId !== routeId ||
                !visibleTask ||
                visibleTask.version !== confirmation.version ||
                !controlAllowed(visibleTask, confirmation.action) ||
                (confirmation.action === 'resume' && !validAllowance(allowance, confirmation.population))
            )
                return;
            frozenOperation.current = Object.freeze({
                taskId: confirmation.taskId,
                action: confirmation.action,
                expectedVersion: confirmation.version,
                key: operationKey(),
                ...(confirmation.action === 'resume' ? { allowance: Number(allowance) } : {}),
            });
        } else if (phase !== 'uncertain') return;
        const operation = frozenOperation.current;
        if (operation.taskId !== latestId.current) return;
        const owner = begin(operation.action);
        if (!owner) return;
        setPhase('sending');
        setBanner(null);
        const config = { headers: { 'Idempotency-Key': operation.key }, cancelToken: owner.token };
        const data = {
            expected_version: operation.expectedVersion,
            ...(operation.action === 'resume' ? { allowance: operation.allowance } : {}),
        };
        try {
            const url = `${endpoint.FW_TASKS}/${operation.taskId}`;
            const response =
                operation.action === 'retire'
                    ? await axiosInstance.delete(url, { ...config, data })
                    : await axiosInstance.post(`${url}/${operation.action}`, data, config);
            if (!owns(owner)) return;
            const result = response?.data;
            const recorded =
                result?.code === 'FW_OPERATION_RECORDED' &&
                identifier(result?.data?.task_id) === operation.taskId &&
                result?.data?.version === operation.expectedVersion + 1;
            if (!recorded) {
                setPhase('blocked');
                setTask(null);
                setBanner(copy.unknown);
                return;
            }
            if (operation.action === 'retire') {
                frozenOperation.current = null;
                setConfirmation(null);
                finishOwner(owner);
                recordTaskRetirement(history, operation.taskId);
                history.push('/fw-tasks');
            } else {
                setOutcome(controlCopy[operation.action].outcome);
                finishOwner(owner);
                await loadTask();
            }
        } catch (error) {
            if (!owns(owner) || isCancelled(error)) return;
            const refusal = refusalOf(error);
            const message = controlErrorText(refusal);
            if (isDisabledRefusal(refusal)) {
                frozenOperation.current = null;
                setConfirmation(null);
                setPhase('editing');
                setTask(null);
                setMode(workflowModes.DISABLED);
                setBanner(message);
            } else if (refusal.status === null || refusal.status >= 500) {
                setPhase('uncertain');
                setBanner(`${message} ${copy.uncertain}`);
            } else {
                setPhase('blocked');
                setTask(null);
                setBanner(`${message} ${copy.reloadRequired}`);
            }
        } finally {
            finishOwner(owner);
        }
    };

    const statusRenderer = params => {
        const color = fwDeviceStatusColors[params.value] || '#000';
        return `<span style="color: ${color}">${fwDeviceStatusLabels[params.value] || params.value}</span>`;
    };

    const serialRenderer = params => {
        const serial = params.value || '';
        if (params.data?.excluded) {
            return `<span style="color: #D32F2F; text-decoration: line-through">${serial}</span>`;
        }
        return serial;
    };

    const taskStatus = visibleTask ? fwTaskStatusValue(visibleTask.status) : null;
    const workflow = mode === workflowModes.CONFIGURED;
    const legacyMode = mode === workflowModes.DISABLED && workflowIdentity.current !== routeId;
    const showFinish =
        visibleTask &&
        (workflow ? controlAllowed(visibleTask, 'finish') : legacyMode && taskStatus === fwTaskStatus.ACTIVE);
    const showPause = visibleTask && workflow && controlAllowed(visibleTask, 'pause');
    const showResume = visibleTask && workflow && controlAllowed(visibleTask, 'resume');
    const missingCycle =
        visibleTask &&
        workflow &&
        ['active', 'paused'].includes(visibleTask.status) &&
        !currentCycle(visibleTask);
    const missingPopulation =
        visibleTask &&
        workflow &&
        visibleTask.status === 'paused' &&
        currentCycle(visibleTask) &&
        !(membershipCount(visibleTask) > 0);
    const showRetire = visibleTask && workflow && controlAllowed(visibleTask, 'retire');
    const showDelete =
        visibleTask && legacyMode && [fwTaskStatus.CANCELLED, fwTaskStatus.COMPLETED].includes(taskStatus);
    const fwFile = visibleTask?.fw_file;
    const controlsBusy = busy || Boolean(confirmation) || mode === workflowModes.LOADING;
    const activeConfirmation = confirmation?.taskId === routeId ? confirmation : null;
    const confirmationCopy = controlCopy[activeConfirmation?.action];
    const invalidAllowance =
        activeConfirmation?.action === 'resume' && !validAllowance(allowance, activeConfirmation.population);

    return (
        <div className={classes.root}>
            <TitleHeader
                title={visibleTask ? `Moduł FW — Zadanie #${visibleTask.id}` : 'Moduł FW — Zadanie'}
            />
            {outcome && <Typography role="status">{outcome}</Typography>}
            {!activeConfirmation && banner && (
                <Typography role="alert" color="error">
                    {banner}
                </Typography>
            )}
            {mode === workflowModes.LOADING && (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            )}
            {visibleTask && (
                <>
                    <div className={classes.info}>
                        <Typography>{`Nazwa zadania: ${visibleTask.name}`}</Typography>
                        <Typography>{`Status zadania: ${fwTaskStatusLabels[taskStatus] || visibleTask.status}`}</Typography>
                        <Typography>{`Plik .bin: ${fwFile?.filename ?? '—'}`}</Typography>
                        <Typography>{`Typ urządzenia: ${fwTaskTypeLabels[visibleTask.firmware_type] || visibleTask.firmware_type}`}</Typography>
                        <Typography>{`Rozmiar: ${fwFile?.file_size ?? '—'}`}</Typography>
                        <Typography>{`CRC-32: ${fwFile?.file_crc32 ?? '—'}`}</Typography>
                    </div>
                    {missingCycle && <Typography role="alert">{copy.cycleUnavailable}</Typography>}
                    {missingPopulation && <Typography role="alert">{copy.populationUnavailable}</Typography>}
                    <div className={classes.actions}>
                        {showPause && (
                            <Button
                                variant="outlined"
                                disabled={controlsBusy}
                                onClick={() => startControl('pause')}>
                                {copy.pause}
                            </Button>
                        )}
                        {showResume && (
                            <Button
                                variant="outlined"
                                disabled={controlsBusy}
                                onClick={() => startControl('resume')}>
                                {copy.resume}
                            </Button>
                        )}

                        {showFinish && (
                            <Button
                                variant="contained"
                                color="primary"
                                disabled={controlsBusy}
                                onClick={() =>
                                    workflow ? startControl('finish') : setIsFinishModalOpen(true)
                                }>
                                {copy.finish}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            disabled={controlsBusy}
                            onClick={() => (workflow ? loadTask() : runLegacy('refresh'))}>
                            Odśwież statusy
                        </Button>
                        {showRetire && (
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={controlsBusy}
                                onClick={() => startControl('retire')}>
                                {copy.retire}
                            </Button>
                        )}
                        {showDelete && (
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={controlsBusy}
                                onClick={() => {
                                    if (window.confirm('Usunąć zadanie?')) runLegacy('delete');
                                }}>
                                Usuń
                            </Button>
                        )}
                    </div>
                    {workflow && <FwTaskStarts key={`${routeId}:${readRevision}`} taskId={visibleTask.id} />}
                    {workflow ? (
                        <FwEndpointBoard key={`board:${routeId}:${readRevision}`} taskId={visibleTask.id} />
                    ) : (
                        <>
                            <Typography variant="h6">Lista numerów seryjnych zadania</Typography>
                            <div className={'ag-theme-alpine'} style={styles.table}>
                                <AgGridReact
                                    localeText={tableTranslations}
                                    rowData={Array.isArray(visibleTask.devices) ? visibleTask.devices : []}
                                    domLayout="autoHeight"
                                    suppressCellSelection
                                    pagination
                                    paginationPageSize={50}
                                    enableCellTextSelection
                                    accentedSort>
                                    <AgGridColumn
                                        field="serial_number"
                                        headerName="Numer seryjny"
                                        flex={1}
                                        sortable
                                        filter
                                        cellRenderer={serialRenderer}
                                    />
                                    <AgGridColumn
                                        field="status"
                                        headerName="Status Endpointa"
                                        width={170}
                                        sortable
                                        filter
                                        cellRenderer={statusRenderer}
                                    />
                                    <AgGridColumn
                                        field="error_message"
                                        headerName="Błąd"
                                        flex={1}
                                        sortable
                                        filter
                                    />
                                </AgGridReact>
                            </div>
                        </>
                    )}
                </>
            )}
            {!activeConfirmation && (
                <Button disabled={busy || mode === workflowModes.LOADING} onClick={loadTask}>
                    {copy.reload}
                </Button>
            )}
            <Button disabled={busy} onClick={() => history.push('/fw-tasks')}>
                {copy.back}
            </Button>
            <Dialog
                open={Boolean(activeConfirmation)}
                onClose={dismissControl}
                disableEscapeKeyDown={busy}
                aria-labelledby="fw-task-control-title"
                TransitionProps={{ onEntered: () => cancelButton.current?.focus() }}
                fullWidth>
                <DialogTitle id="fw-task-control-title">
                    {confirmationCopy?.confirm || copy.unknown}
                </DialogTitle>
                <DialogContent>
                    <Typography>{`${copy.identity}: ${activeConfirmation?.taskId}; ${copy.name}: ${activeConfirmation?.name}`}</Typography>
                    <Typography>{`${copy.status}: ${fwTaskStatusLabels[fwTaskStatusValue(activeConfirmation?.status)] ?? ''}; ${copy.version}: ${activeConfirmation?.version}`}</Typography>
                    <Typography>{confirmationCopy?.effect || copy.unknown}</Typography>
                    {['pause', 'resume'].includes(activeConfirmation?.action) && (
                        <Typography>{`${copy.cycleIdentity}: ${activeConfirmation.cycleId}`}</Typography>
                    )}
                    {activeConfirmation?.action === 'resume' && (
                        <TextField
                            id="fw-task-resume-allowance"
                            label={copy.allowance}
                            value={allowance}
                            onChange={event => {
                                if (!mutationOwner.current && !frozenOperation.current && phase === 'editing')
                                    setAllowance(event.target.value);
                            }}
                            disabled={busy || phase !== 'editing'}
                            required
                            variant="outlined"
                            fullWidth
                            inputProps={{ inputMode: 'numeric' }}
                            error={allowance !== '' && invalidAllowance}
                            helperText={`${copy.population}: ${activeConfirmation.population}. ${invalidAllowance ? copy.allowanceInvalid : copy.allowanceHint}`}
                        />
                    )}
                    {banner && (
                        <Typography role="alert" color="error">
                            {banner}
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {phase === 'editing' || phase === 'sending' ? (
                        <>
                            <Button ref={cancelButton} disabled={busy} onClick={dismissControl}>
                                {copy.cancel}
                            </Button>
                            <Button disabled={busy || invalidAllowance} onClick={sendControl}>
                                {confirmationCopy?.confirm || copy.unknown}
                            </Button>
                        </>
                    ) : (
                        <>
                            {phase === 'uncertain' && (
                                <Button disabled={busy} onClick={sendControl}>
                                    {copy.retry}
                                </Button>
                            )}
                            <Button disabled={busy} onClick={loadTask}>
                                {copy.reload}
                            </Button>
                            <Button disabled={busy} onClick={dismissControl}>
                                {copy.close}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>
            <Modal
                isModalOpen={Boolean(isFinishModalOpen && visibleTask && legacyMode)}
                isSpinnerActive={busy}
                handleAnswer={confirmed => {
                    setIsFinishModalOpen(false);
                    if (confirmed) runLegacy('finish');
                }}
                modalMessage="Czy zakończyć zadanie?"
            />
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

export default withStyles(styles)(FwTaskDetail);
